import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ChevronRight,
  Crown,
  Info,
  Plus,
  Search,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Trophy,
  Users,
  X,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@v2/components/ui/avatar";
import { Badge } from "@v2/components/ui/badge";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@v2/components/ui/dialog";
import { Input } from "@v2/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@v2/components/ui/sheet";
import { cn } from "@v2/lib/utils";
import { supabaseV2 } from "../../lib/supabase";
import { useWorkspaceData } from "../../lib/workspace-data";
import { EmptyState, SectionLoading } from "../../components/state";
import { RadarChart, type RadarDataset } from "../../components/charts";
import {
  EA_POSITIONS,
  COLORS,
  BENCHMARK_KEYS,
  aggregateScore,
  computeBenchmark,
  isRefinementVisible,
  periodToDays,
  radarValuesFor,
  refinedKpi,
  reliabilityOf,
  statValueOf,
  type Benchmark,
  type EaPosition,
  type PeriodKey,
  type PositionStats,
} from "./comparison-math";

type PositionsPlayedRow = {
  player_id: string;
  display_name: string;
  position: string;
  matches: number;
};

const MAX_COMPARE = 4;

// Hook minimale per rilevare viewport mobile. Aggiorna su resize.
// Soglia 768px coerente col `lg:` breakpoint di tailwind (che è 1024px ma
// qui voglio già su tablet portrait dare il layout compatto).
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(`(max-width: ${breakpoint - 1}px)`).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [breakpoint]);
  return isMobile;
}

// Helper matematici (refinement, score, normalize, ecc.) sono in
// ./comparison-math.ts e riusati anche dal Top 11. Qui restano solo le
// funzioni specifiche del Confronto, di seguito.

// KPI principali per ruolo usati nel pannello "delta vs squadra" del player
// highlight. Sempre 3, e sempre presenti nel benchmark calcolato lato
// componente principale. Per "any" niente delta (manca il benchmark).
type DeltaKey = keyof Benchmark;
function deltaKeysFor(position: EaPosition | "any"): DeltaKey[] {
  switch (position) {
    case "goalkeeper":
      return ["avg_rating", "win_rate", "mom_per_match"];
    case "defender":
      return ["avg_rating", "win_rate", "tackle_pct"];
    case "midfielder":
      return ["avg_rating", "win_rate", "pass_pct"];
    case "forward":
      return ["avg_rating", "goals_per_match", "assists_per_match"];
    default:
      return [];
  }
}

// Valore del player per un KPI presente nel benchmark, in versione
// "raffinata" (top-10 rating + shrinkage o solo shrinkage). Senza benchmark
// si ricade sul nudo.
function playerValueFor(s: PositionStats, key: DeltaKey, benchmark: Benchmark | null): number {
  return refinedKpi(s, key, benchmark);
}

function formatMetricValue(key: DeltaKey, v: number): string {
  switch (key) {
    case "avg_rating":
      return v.toFixed(2);
    case "win_rate":
    case "mom_per_match":
    case "pass_pct":
    case "tackle_pct":
      return `${v.toFixed(0)}%`;
    case "goals_per_match":
    case "assists_per_match":
      return v.toFixed(2);
  }
}

export function PlayersComparisonView({
  teamId,
  onOpenPlayer,
}: {
  teamId: string;
  onOpenPlayer: (playerId: string) => void;
}) {
  const { t } = useTranslation("players");
  const { players, isLoading } = useWorkspaceData(teamId);
  const isMobile = useIsMobile();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [position, setPosition] = useState<EaPosition | "any">("any");
  const [period, setPeriod] = useState<PeriodKey>("all");
  const [search, setSearch] = useState("");
  const [listFilterPosition, setListFilterPosition] = useState<EaPosition | "any">("any");
  const [infoOpen, setInfoOpen] = useState(false);
  // Su mobile la selezione player vive in un bottom sheet, qui controllo l'apertura
  const [selectSheetOpen, setSelectSheetOpen] = useState(false);

  // Auto-open del dialog "Come si conta" alla primissima visita della sezione,
  // così l'utente capisce da subito che si lavora solo sui match EA. Una volta
  // chiuso il dialog, il flag in localStorage evita di riproporlo (resta
  // comunque il pulsante visibile in alto per chi vuole rileggere).
  useEffect(() => {
    try {
      if (window.localStorage.getItem("eb-compare-info-seen-v4") !== "1") {
        setInfoOpen(true);
      }
    } catch {
      /* ignore */
    }
  }, []);
  const handleInfoOpenChange = (open: boolean) => {
    setInfoOpen(open);
    if (!open) {
      try {
        window.localStorage.setItem("eb-compare-info-seen-v4", "1");
      } catch {
        /* ignore */
      }
    }
  };

  const positionsPlayedQuery = useQuery({
    queryKey: ["roster-positions-played", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("team_player_positions_played", {
        p_team_id: teamId,
        p_player_ids: null,
      });
      if (error) throw error;
      return (data || []) as PositionsPlayedRow[];
    },
    staleTime: 60_000,
  });
  const positionsPlayed = positionsPlayedQuery.data || [];

  const statsQuery = useQuery({
    queryKey: ["roster-position-stats", teamId, selectedIds, position, period],
    enabled: selectedIds.length >= 1,
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("team_player_position_stats", {
        p_team_id: teamId,
        p_player_ids: selectedIds,
        p_position: position === "any" ? null : position,
        p_period_days: periodToDays(period),
      });
      if (error) throw error;
      return data as { players: PositionStats[]; position: string };
    },
    staleTime: 30_000,
  });

  // Benchmark del ruolo: aggregato del team intero filtrato sulla posizione.
  // Includo i player con almeno uno tra ea_account_handle e ea_sports_id
  // (la RPC matcha contro entrambi).
  const allRosterIds = useMemo(
    () => players.filter((p) => p.ea_account_handle || p.ea_sports_id).map((p) => p.id),
    [players],
  );
  const benchmarkQuery = useQuery({
    queryKey: ["roster-position-benchmark", teamId, position, period, allRosterIds.length],
    enabled: position !== "any" && allRosterIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("team_player_position_stats", {
        p_team_id: teamId,
        p_player_ids: allRosterIds,
        p_position: position,
        p_period_days: periodToDays(period),
      });
      if (error) throw error;
      return data as { players: PositionStats[] };
    },
    staleTime: 30_000,
  });
  const benchmark: Benchmark | null = useMemo(
    () => computeBenchmark(benchmarkQuery.data?.players || []),
    [benchmarkQuery.data],
  );

  const positionsByPlayer = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const r of positionsPlayed) {
      if (!m.has(r.player_id)) m.set(r.player_id, new Set());
      m.get(r.player_id)!.add(r.position);
    }
    return m;
  }, [positionsPlayed]);

  const eligible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return players
      .filter((p) =>
        !needle ||
        p.display_name.toLowerCase().includes(needle) ||
        (p.ea_account_handle || "").toLowerCase().includes(needle) ||
        (p.ea_sports_id || "").toLowerCase().includes(needle),
      )
      .filter((p) => {
        if (listFilterPosition === "any") return true;
        const set = positionsByPlayer.get(p.id);
        return set?.has(listFilterPosition) ?? false;
      })
      .sort((a, b) => {
        const aHas = !!(a.ea_account_handle || a.ea_sports_id);
        const bHas = !!(b.ea_account_handle || b.ea_sports_id);
        if (aHas !== bHas) return aHas ? -1 : 1;
        return a.display_name.localeCompare(b.display_name);
      });
  }, [players, search, listFilterPosition, positionsByPlayer]);

  const positionsForSelection = useMemo(() => {
    if (selectedIds.length === 0) return EA_POSITIONS;
    const setPos = new Set<string>();
    for (const r of positionsPlayed) {
      if (selectedIds.includes(r.player_id) && r.position) setPos.add(r.position);
    }
    return EA_POSITIONS.filter((p) => setPos.has(p));
  }, [selectedIds, positionsPlayed]);

  useEffect(() => {
    if (position !== "any" && !positionsForSelection.includes(position)) {
      setPosition("any");
    }
  }, [position, positionsForSelection]);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_COMPARE) return prev;
      return [...prev, id];
    });
  };
  const removeSelected = (id: string) => {
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  };
  const clearSelected = () => setSelectedIds([]);

  const stats = statsQuery.data?.players || [];
  const enoughData = selectedIds.length >= 2;
  const lowSampleThreshold = 5;

  // Etichette assi radar (desktop) e versione compatta (mobile). Le 6 metriche
  // sono diverse per ruolo: DEF privilegia tackle%/pass%, GK clean sheets/parate,
  // MID + FWD mantengono il set offensivo (goals/m, assists/m). Devono restare
  // SINCRONIZZATE con `radarValuesFor()` in comparison-math.ts.
  const radarAxesLong =
    position === "goalkeeper"
      ? [t("compare.metric.avgRating"), t("compare.metric.winRate"), t("compare.metric.cleanSheetsPerMatch"), t("compare.metric.savesPerMatch"), t("compare.metric.momPct"), t("compare.metric.minutesAvg")]
      : position === "defender"
        ? [t("compare.metric.avgRating"), t("compare.metric.winRate"), t("compare.metric.tacklePct"), t("compare.metric.passPct"), t("compare.metric.momPct"), t("compare.metric.assistsPerMatch")]
        : [t("compare.metric.avgRating"), t("compare.metric.winRate"), t("compare.metric.goalsPerMatch"), t("compare.metric.assistsPerMatch"), t("compare.metric.momPct"), t("compare.metric.passPct")];
  // Su mobile uso sigle per non far esplodere il radar.
  const radarAxesShort =
    position === "goalkeeper"
      ? ["Rating", "Win%", "CS/G", "Par/G", "MOTM", "Min"]
      : position === "defender"
        ? ["Rating", "Win%", "Tkl%", "Pass%", "MOTM", "A/G"]
        : ["Rating", "Win%", "G/G", "A/G", "MOTM", "Pass%"];

  const radarDatasets: RadarDataset[] = stats.map((s, idx) => ({
    label: s.display_name,
    values: position === "any" ? [] : radarValuesFor(s, position, benchmark),
    color: COLORS[idx % COLORS.length],
  }));

  // Lookup selezionati come player records (per chip header).
  const selectedPlayers = useMemo(
    () => selectedIds.map((id) => players.find((p) => p.id === id)).filter(Boolean) as typeof players,
    [selectedIds, players],
  );

  if (isLoading) return <SectionLoading />;

  return (
    <div className="space-y-4 animate-fade-in">
      <section>
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <h2 className="text-2xl font-semibold">{t("compare.title")}</h2>
          <button
            type="button"
            onClick={() => setInfoOpen(true)}
            className="inline-flex items-center gap-1 rounded-full border bg-card hover:bg-muted/60 px-2.5 py-1 text-xs font-medium text-foreground/80 transition-smooth"
          >
            <Info className="h-3.5 w-3.5" />
            {t("compare.info.cta")}
          </button>
        </div>
        <p className="text-sm text-muted-foreground">{t("compare.subtitle")}</p>
      </section>

      <Dialog open={infoOpen} onOpenChange={handleInfoOpenChange}>
        <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("compare.info.title")}</DialogTitle>
            <DialogDescription>{t("compare.info.intro")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <section className="space-y-1.5">
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
                {t("compare.info.included.title")}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {t("compare.info.included.body")}
              </p>
            </section>
            <section className="space-y-1.5">
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
                {t("compare.info.excluded.title")}
              </h3>
              <ul className="text-muted-foreground leading-relaxed list-disc pl-5 space-y-1">
                <li>{t("compare.info.excluded.manual")}</li>
                <li>{t("compare.info.excluded.career")}</li>
              </ul>
            </section>
            <section className="space-y-1.5">
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
                {t("compare.info.filters.title")}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {t("compare.info.filters.body")}
              </p>
            </section>
            {/* Le 2 sezioni qui sotto sono cruciali per la trasparenza del
                confronto: spiegano perché un valore mostrato in app può non
                coincidere con la "media nuda" che si trova altrove (Mondo FC,
                EA Companion ecc.). Mantenerle sempre in cima e dopo i filtri. */}
            <section className="space-y-1.5 rounded-lg border border-primary/30 bg-primary/5 p-3">
              <h3 className="text-xs uppercase tracking-wide text-primary font-semibold flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" />
                {t("compare.info.rating.title")}
              </h3>
              <p className="text-foreground/90 leading-relaxed">
                {t("compare.info.rating.body")}
              </p>
              <p className="text-muted-foreground leading-relaxed text-xs">
                {t("compare.info.rating.example")}
              </p>
            </section>
            <section className="space-y-1.5 rounded-lg border border-primary/30 bg-primary/5 p-3">
              <h3 className="text-xs uppercase tracking-wide text-primary font-semibold flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" />
                {t("compare.info.shrinkage.title")}
              </h3>
              <p className="text-foreground/90 leading-relaxed">
                {t("compare.info.shrinkage.body")}
              </p>
              <p className="text-muted-foreground leading-relaxed text-xs">
                {t("compare.info.shrinkage.example")}
              </p>
              <p className="text-muted-foreground leading-relaxed text-xs italic">
                <span className="text-primary font-semibold">*</span> {t("compare.info.shrinkage.markLegend")}
              </p>
            </section>
            <section className="space-y-1.5 rounded-lg border border-primary/30 bg-primary/5 p-3">
              <h3 className="text-xs uppercase tracking-wide text-primary font-semibold flex items-center gap-1.5">
                <Trophy className="h-3.5 w-3.5" />
                {t("compare.info.score.title")}
              </h3>
              <p className="text-foreground/90 leading-relaxed">
                {t("compare.info.score.body")}
              </p>
              <p className="text-muted-foreground leading-relaxed text-xs">
                {t("compare.info.score.confidence")}
              </p>
            </section>
            <section className="space-y-1.5">
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
                {t("compare.info.limits.title")}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {t("compare.info.limits.body")}
              </p>
            </section>
            <div className="pt-2">
              <Button
                type="button"
                className="w-full"
                onClick={() => handleInfoOpenChange(false)}
              >
                {t("compare.info.gotIt")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* MOBILE: header riassuntivo con chip dei selezionati + bottone aggiungi.
          Su desktop nascosto in favore del pannello classico. */}
      {isMobile && (
        <Card className="shadow-card">
          <CardContent className="p-3 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              {selectedPlayers.length === 0 ? (
                <p className="text-sm text-muted-foreground flex-1">
                  {t("compare.mobile.sheetDesc", { max: MAX_COMPARE })}
                </p>
              ) : (
                selectedPlayers.map((p, idx) => (
                  <SelectedChip
                    key={p.id}
                    label={p.display_name}
                    color={COLORS[idx % COLORS.length]}
                    onRemove={() => removeSelected(p.id)}
                  />
                ))
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant={selectedPlayers.length === 0 ? "default" : "outline"}
                className="flex-1"
                onClick={() => setSelectSheetOpen(true)}
              >
                <Plus className="h-4 w-4 mr-1.5" />
                {selectedPlayers.length === 0
                  ? t("compare.mobile.selectPlayers")
                  : t("compare.mobile.addPlayer")}
              </Button>
              {selectedPlayers.length > 0 && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={clearSelected}
                  className="text-muted-foreground"
                >
                  {t("compare.mobile.clearAll")}
                </Button>
              )}
            </div>
            {/* Filtri pos/period spostati qui su mobile: sempre visibili,
                niente nesting con la lista player. */}
            {selectedPlayers.length >= 1 && (
              <FiltersInline
                position={position}
                onPositionChange={setPosition}
                period={period}
                onPeriodChange={setPeriod}
                positionsForSelection={positionsForSelection}
                t={t}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* MOBILE: bottom-sheet di selezione, riusa la lista filtrata `eligible`. */}
      {isMobile && (
        <Sheet open={selectSheetOpen} onOpenChange={setSelectSheetOpen}>
          <SheetContent
            side="bottom"
            className="h-[85dvh] p-0 flex flex-col rounded-t-2xl border-0 sm:max-w-none"
          >
            <SheetHeader className="px-4 pt-4 pb-2 text-left">
              <SheetTitle>{t("compare.mobile.selectPlayers")}</SheetTitle>
              <SheetDescription>
                {t("compare.mobile.sheetDesc", { max: MAX_COMPARE })}
              </SheetDescription>
            </SheetHeader>
            <div className="px-4 pt-2 space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("compare.config.searchPh")}
                  className="pl-8 h-9"
                />
              </div>
              <RolePillRow
                value={listFilterPosition}
                onChange={setListFilterPosition}
                t={t}
              />
            </div>
            <div className="flex-1 overflow-y-auto px-2 pt-2 pb-4 divide-y divide-border/40">
              {eligible.length === 0 && (
                <p className="text-xs text-muted-foreground p-3 text-center">
                  {t("compare.config.noEligible")}
                </p>
              )}
              {eligible.map((p) => {
                const noEa = !p.ea_account_handle && !p.ea_sports_id;
                const isSelected = selectedIds.includes(p.id);
                const reachedMax = !isSelected && selectedIds.length >= MAX_COMPARE;
                return (
                  <button
                    key={p.id}
                    type="button"
                    disabled={reachedMax}
                    onClick={() => toggle(p.id)}
                    className={cn(
                      "w-full flex items-center gap-3 px-2 py-3 text-left transition-colors text-sm rounded-md",
                      isSelected ? "bg-primary/10" : "hover:bg-muted/40",
                      reachedMax && !isSelected && "opacity-50 cursor-not-allowed",
                    )}
                  >
                    <Avatar className="h-9 w-9">
                      <AvatarFallback>{p.display_name.slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate flex items-center gap-1.5">
                        <span className="truncate">{p.display_name}</span>
                        {p.is_guest && (
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">
                            {t("compare.guestTag")}
                          </span>
                        )}
                        {noEa && (
                          <span className="rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 px-1 py-0.5 text-[9px] font-semibold shrink-0">
                            {t("compare.config.noEaBadge")}
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-muted-foreground font-mono truncate">
                        {p.ea_account_handle || p.ea_sports_id || t("compare.config.noEaHandle")}
                        {p.role_code && ` · ${p.role_code}`}
                      </p>
                    </div>
                    {isSelected && (
                      <span className="h-2 w-2 rounded-full bg-primary shrink-0" aria-hidden />
                    )}
                  </button>
                );
              })}
            </div>
            <div className="border-t bg-background px-4 py-3 sticky bottom-0">
              <Button
                type="button"
                className="w-full"
                disabled={selectedPlayers.length === 0}
                onClick={() => setSelectSheetOpen(false)}
              >
                {t("compare.mobile.done")} ({selectedPlayers.length})
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* DESKTOP: pannello configurazione completo (versione storica). */}
      {!isMobile && (
        <Card className="shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              {t("compare.config.title")}
            </CardTitle>
            <CardDescription>{t("compare.config.desc", { max: MAX_COMPARE })}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("compare.config.searchPh")}
                  className="pl-8 h-9"
                />
              </div>
              <div className="space-y-1">
                <p className="text-[11px] font-medium text-muted-foreground">
                  {t("compare.config.listFilter")}
                </p>
                <RolePillRow
                  value={listFilterPosition}
                  onChange={setListFilterPosition}
                  t={t}
                />
              </div>
              <div className="max-h-64 overflow-y-auto rounded-md border bg-card/60 divide-y divide-border/40">
                {eligible.length === 0 && (
                  <p className="text-xs text-muted-foreground p-3 text-center">
                    {t("compare.config.noEligible")}
                  </p>
                )}
                {eligible.map((p) => {
                  const noEa = !p.ea_account_handle && !p.ea_sports_id;
                  const isSelected = selectedIds.includes(p.id);
                  const reachedMax = !isSelected && selectedIds.length >= MAX_COMPARE;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      disabled={reachedMax}
                      onClick={() => toggle(p.id)}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2 text-left transition-colors text-sm",
                        isSelected ? "bg-primary/10" : "hover:bg-muted/40",
                        reachedMax && !isSelected && "opacity-50 cursor-not-allowed",
                      )}
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>{p.display_name.slice(0, 2).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate flex items-center gap-1.5">
                          <span className="truncate">{p.display_name}</span>
                          {p.is_guest && (
                            <span className="text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">
                              {t("compare.guestTag")}
                            </span>
                          )}
                          {noEa && (
                            <span className="rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 px-1 py-0.5 text-[9px] font-semibold shrink-0">
                              {t("compare.config.noEaBadge")}
                            </span>
                          )}
                        </p>
                        <p className="text-[11px] text-muted-foreground font-mono truncate">
                          {p.ea_account_handle || p.ea_sports_id || t("compare.config.noEaHandle")}
                          {p.role_code && ` · ${p.role_code}`}
                        </p>
                      </div>
                      {isSelected && (
                        <span className="h-2 w-2 rounded-full bg-primary shrink-0" aria-hidden />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedIds.length >= 1 && (
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground">
                    {t("compare.config.position")}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    <PillButton
                      active={position === "any"}
                      onClick={() => setPosition("any")}
                    >
                      {t("compare.position.any")}
                    </PillButton>
                    {positionsForSelection.map((p) => (
                      <PillButton
                        key={p}
                        active={position === p}
                        onClick={() => setPosition(p)}
                      >
                        {t(`compare.position.${p}`)}
                      </PillButton>
                    ))}
                  </div>
                  {selectedIds.length > 0 && positionsForSelection.length === 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      {t("compare.config.noPositionsForSelection")}
                    </p>
                  )}
                  {position === "any" && positionsForSelection.length > 0 && (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-700 dark:text-amber-400 mt-1">
                      <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      <span className="leading-snug">{t("compare.config.pickPositionHint")}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground">
                    {t("compare.config.period")}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {(["30", "90", "season", "all"] as PeriodKey[]).map((p) => (
                      <PillButton
                        key={p}
                        active={period === p}
                        onClick={() => setPeriod(p)}
                      >
                        {t(`compare.period.${p}`)}
                      </PillButton>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!enoughData && (
        <Card className="shadow-card">
          <CardContent className="py-8">
            <EmptyState text={t("compare.empty")} />
          </CardContent>
        </Card>
      )}

      {/* Risultati */}
      {enoughData && statsQuery.isLoading && <SectionLoading />}
      {enoughData && !statsQuery.isLoading && stats.length === 0 && (
        <Card className="shadow-card">
          <CardContent className="py-6">
            <EmptyState text={t("compare.noStats")} />
          </CardContent>
        </Card>
      )}

      {enoughData && stats.length >= 2 && (
        <>
          {/* Avvisi sample piccolo */}
          {stats.some((s) => s.matches < lowSampleThreshold) && (
            <div className="rounded-lg border bg-amber-500/10 border-amber-500/30 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
              {t("compare.lowSampleWarning", { min: lowSampleThreshold })}
            </div>
          )}

          {/* Verdetto in cima: chi vince questa selezione nel ruolo */}
          {position !== "any" && (
            <VerdictCard
              stats={stats}
              position={position}
              period={period}
              benchmark={benchmark}
              t={t}
              onOpenPlayer={onOpenPlayer}
            />
          )}

          {/* Radar + box per-player (forza/debolezza/delta vs squadra) */}
          {position !== "any" && (
            <Card className="shadow-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{t("compare.radar.title")}</CardTitle>
                <CardDescription>
                  {t("compare.radar.desc", { position: t(`compare.position.${position}`) })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4 items-start">
                  <div className={cn("w-full mx-auto lg:mx-0", isMobile ? "max-w-[280px]" : "max-w-[420px]")}>
                    <RadarChart
                      axes={isMobile ? radarAxesShort : radarAxesLong}
                      datasets={radarDatasets}
                      size={isMobile ? 240 : 320}
                    />
                  </div>
                  <div className="space-y-3">
                    {stats.map((s, idx) => (
                      <PlayerHighlightCard
                        key={s.player_id}
                        stat={s}
                        position={position}
                        benchmark={benchmark}
                        color={COLORS[idx % COLORS.length]}
                        otherStats={stats.filter((o) => o.player_id !== s.player_id)}
                        t={t}
                      />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tabella comparativa: desktop = tabella classica, mobile = stack di metriche */}
          {isMobile ? (
            <MetricStack
              stats={stats}
              position={position}
              benchmark={benchmark}
              t={t}
            />
          ) : (
            <Card className="shadow-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{t("compare.table.title")}</CardTitle>
                <CardDescription>{t("compare.table.desc")}</CardDescription>
              </CardHeader>
              <CardContent>
                <ComparisonTable
                  stats={stats}
                  position={position}
                  benchmark={benchmark}
                  onOpenPlayer={onOpenPlayer}
                  t={t}
                />
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers UI riutilizzati: pill bottoni, chip selezionati, riga pill ruoli
// ──────────────────────────────────────────────────────────────────────────

// Asterisco accanto al label di metrica "raffinata", per indicare che il
// numero mostrato non è la media nuda ma è top10 + shrinkage. Tooltip con
// la spiegazione. Centralizzato così appare identico ovunque.
function RefinedMark({ t }: { t: (k: string, opts?: any) => string }) {
  return (
    <span
      className="ml-1 text-primary/70 align-baseline cursor-help"
      title={t("compare.refinement.headerTip")}
      aria-label={t("compare.refinement.headerTip")}
    >
      *
    </span>
  );
}

function PillButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium transition-smooth",
        active
          ? "border-primary bg-primary/10 text-primary"
          : "bg-card hover:bg-muted/60 text-foreground/80",
      )}
    >
      {children}
    </button>
  );
}

function SelectedChip({
  label,
  color,
  onRemove,
}: {
  label: string;
  color: string;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border bg-card pl-2 pr-1 py-1 text-xs font-medium">
      <span
        className="h-2 w-2 rounded-full shrink-0"
        style={{ background: color }}
        aria-hidden
      />
      <span className="truncate max-w-[120px]">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="rounded-full hover:bg-muted p-0.5 text-muted-foreground hover:text-foreground transition-colors"
        aria-label="rimuovi"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

function RolePillRow({
  value,
  onChange,
  t,
}: {
  value: EaPosition | "any";
  onChange: (v: EaPosition | "any") => void;
  t: (k: string, opts?: any) => string;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      <PillButton active={value === "any"} onClick={() => onChange("any")}>
        {t("compare.position.any")}
      </PillButton>
      {EA_POSITIONS.map((p) => (
        <PillButton key={p} active={value === p} onClick={() => onChange(p)}>
          {t(`compare.position.${p}`)}
        </PillButton>
      ))}
    </div>
  );
}

function FiltersInline({
  position,
  onPositionChange,
  period,
  onPeriodChange,
  positionsForSelection,
  t,
}: {
  position: EaPosition | "any";
  onPositionChange: (v: EaPosition | "any") => void;
  period: PeriodKey;
  onPeriodChange: (v: PeriodKey) => void;
  positionsForSelection: readonly EaPosition[];
  t: (k: string, opts?: any) => string;
}) {
  return (
    <div className="space-y-2 pt-1">
      <div className="space-y-1">
        <p className="text-[11px] font-medium text-muted-foreground">
          {t("compare.config.position")}
        </p>
        <div className="flex flex-wrap gap-1.5">
          <PillButton active={position === "any"} onClick={() => onPositionChange("any")}>
            {t("compare.position.any")}
          </PillButton>
          {positionsForSelection.map((p) => (
            <PillButton
              key={p}
              active={position === p}
              onClick={() => onPositionChange(p)}
            >
              {t(`compare.position.${p}`)}
            </PillButton>
          ))}
        </div>
        {position === "any" && positionsForSelection.length > 0 && (
          <div className="flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-700 dark:text-amber-400 mt-1">
            <Info className="h-3 w-3 shrink-0 mt-0.5" />
            <span className="leading-snug">{t("compare.config.pickPositionHint")}</span>
          </div>
        )}
      </div>
      <div className="space-y-1">
        <p className="text-[11px] font-medium text-muted-foreground">
          {t("compare.config.period")}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {(["30", "90", "season", "all"] as PeriodKey[]).map((p) => (
            <PillButton key={p} active={period === p} onClick={() => onPeriodChange(p)}>
              {t(`compare.period.${p}`)}
            </PillButton>
          ))}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Verdict card: chi vince questa selezione nel ruolo, con score 0-100 e
// distacco sul secondo. Sample low warning incorporato.
// ──────────────────────────────────────────────────────────────────────────

function VerdictCard({
  stats,
  position,
  period,
  benchmark,
  t,
  onOpenPlayer,
}: {
  stats: PositionStats[];
  position: EaPosition;
  period: PeriodKey;
  benchmark: Benchmark | null;
  t: (k: string, opts?: any) => string;
  onOpenPlayer: (id: string) => void;
}) {
  const ranked = useMemo(() => {
    return stats
      .map((s, idx) => ({
        stat: s,
        score: aggregateScore(s, position, benchmark),
        color: COLORS[idx % COLORS.length],
      }))
      .sort((a, b) => b.score - a.score);
  }, [stats, position, benchmark]);
  if (ranked.length === 0) return null;
  const top = ranked[0];
  const second = ranked[1];
  // Considera "tie" se la differenza è < 1 punto su 100 (cioè praticamente nulla).
  const isTie = second && Math.abs(top.score - second.score) < 1;
  const lead = second ? Math.max(0, top.score - second.score) : 0;
  const periodLabel = t(`compare.period.${period}`);
  const positionLabel = t(`compare.position.${position}`);

  return (
    <Card className="shadow-card overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-primary/15 text-primary p-2 shrink-0">
            <Trophy className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
              {t("compare.verdict.title")}
            </p>
            <button
              type="button"
              onClick={() => onOpenPlayer(top.stat.player_id)}
              className="text-xl font-bold leading-tight inline-flex items-center gap-1 hover:text-primary transition-colors"
            >
              <span
                className="h-2.5 w-2.5 rounded-full shrink-0"
                style={{ background: top.color }}
                aria-hidden
              />
              <span className="truncate">{top.stat.display_name}</span>
              <ChevronRight className="h-4 w-4 opacity-60" />
            </button>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("compare.verdict.subtitle", { position: positionLabel, period: periodLabel })}
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-2xl font-bold tabular-nums text-primary">
              {top.score}
              <span className="text-sm text-muted-foreground font-medium">/100</span>
            </div>
            {!isTie && second && lead > 0 && (
              <p className="text-[11px] text-muted-foreground tabular-nums">
                {t("compare.verdict.leadBy", { gap: lead.toFixed(0) })}
              </p>
            )}
            {isTie && (
              <Badge variant="outline" className="text-[10px] mt-1">
                {t("compare.verdict.tie", { count: ranked.filter((r) => Math.abs(r.score - top.score) < 1).length })}
              </Badge>
            )}
          </div>
        </div>
        {/* Mini-ranking degli altri: utile a vedere il pacchetto, senza scrollare */}
        {ranked.length > 1 && (
          <div className="mt-3 pt-3 border-t flex flex-wrap gap-1.5">
            {ranked.slice(1).map((r) => (
              <div
                key={r.stat.player_id}
                className="inline-flex items-center gap-1.5 rounded-full bg-muted/60 px-2 py-1 text-[11px]"
              >
                <span
                  className="h-1.5 w-1.5 rounded-full shrink-0"
                  style={{ background: r.color }}
                  aria-hidden
                />
                <span className="truncate max-w-[120px]">{r.stat.display_name}</span>
                <span className="tabular-nums text-muted-foreground font-medium">
                  {t("compare.verdict.scoreShort", { score: r.score })}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Player highlight card: forza + debolezza + delta vs media squadra + badge
// affidabilità campione. Sostituisce il vecchio RadarHighlights.
// ──────────────────────────────────────────────────────────────────────────

function PlayerHighlightCard({
  stat,
  position,
  benchmark,
  color,
  otherStats,
  t,
}: {
  stat: PositionStats;
  position: EaPosition;
  benchmark: Benchmark | null;
  color: string;
  otherStats: PositionStats[];
  t: (k: string, opts?: any) => string;
}) {
  // Metriche valutate per forza/debolezza (allineate al radar).
  const isGk = position === "goalkeeper";
  const strengthMetrics: Array<{ key: keyof PositionStats; labelKey: string; fmt: (v: number) => string; higherIsBetter: boolean }> = isGk
    ? [
        { key: "avg_rating",        labelKey: "compare.metric.avgRating",          fmt: (v) => v.toFixed(2),       higherIsBetter: true },
        { key: "win_rate",          labelKey: "compare.metric.winRate",            fmt: (v) => `${v.toFixed(1)}%`, higherIsBetter: true },
        { key: "clean_sheets",      labelKey: "compare.metric.cleanSheets",        fmt: (v) => String(v),          higherIsBetter: true },
        { key: "saves",             labelKey: "compare.metric.saves",              fmt: (v) => String(v),          higherIsBetter: true },
        { key: "mom_per_match",     labelKey: "compare.metric.momPct",             fmt: (v) => `${v.toFixed(1)}%`, higherIsBetter: true },
        { key: "goals_conceded",    labelKey: "compare.metric.goalsConceded",      fmt: (v) => String(v),          higherIsBetter: false },
      ]
    : [
        { key: "avg_rating",        labelKey: "compare.metric.avgRating",       fmt: (v) => v.toFixed(2),       higherIsBetter: true },
        { key: "win_rate",          labelKey: "compare.metric.winRate",         fmt: (v) => `${v.toFixed(1)}%`, higherIsBetter: true },
        { key: "goals_per_match",   labelKey: "compare.metric.goalsPerMatch",   fmt: (v) => v.toFixed(2),       higherIsBetter: true },
        { key: "assists_per_match", labelKey: "compare.metric.assistsPerMatch", fmt: (v) => v.toFixed(2),       higherIsBetter: true },
        { key: "mom_per_match",     labelKey: "compare.metric.momPct",          fmt: (v) => `${v.toFixed(1)}%`, higherIsBetter: true },
        { key: "pass_pct",          labelKey: "compare.metric.passPct",         fmt: (v) => `${v.toFixed(1)}%`, higherIsBetter: true },
      ];

  // Calcolo forza (gap % maggiore) e debolezza (gap % più negativo).
  // Per le metriche del benchmark (rating, win_rate, goals_per_match, ecc.)
  // uso i valori RAFFINATI (top-10 rating + shrinkage). Per metriche cumulative
  // (clean_sheets, saves, goals_conceded) il valore raw è quello giusto.
  let wins = 0;
  let best: { label: string; gapPct: number; fmt: string } | null = null;
  let worst: { label: string; gapPct: number; fmt: string } | null = null;
  for (const m of strengthMetrics) {
    const my = statValueOf(stat, m.key, benchmark);
    const others = otherStats.map((o) => statValueOf(o, m.key, benchmark));
    if (others.length === 0) continue;
    const otherExtreme = m.higherIsBetter ? Math.max(...others) : Math.min(...others);
    const iWin = m.higherIsBetter ? my > otherExtreme : my < otherExtreme;
    if (iWin) wins += 1;
    // Vantaggio relativo: per "higherIsBetter" guarda (my - other)/other; per
    // "lowerIsBetter" guarda (other - my)/other. Ignoro segno qui: > 0 = forza,
    // < 0 = debolezza.
    const gap = m.higherIsBetter ? my - otherExtreme : otherExtreme - my;
    const denom = m.higherIsBetter ? Math.max(1, otherExtreme) : Math.max(1, otherExtreme);
    const gapPct = denom > 0 ? (gap / denom) * 100 : gap;
    if (gap > 0 && (best === null || gapPct > best.gapPct)) {
      best = { label: t(m.labelKey), gapPct, fmt: m.fmt(my) };
    }
    if (gap < 0 && (worst === null || gapPct < worst.gapPct)) {
      worst = { label: t(m.labelKey), gapPct, fmt: m.fmt(my) };
    }
  }

  const reliability = reliabilityOf(stat.matches);
  const reliabilityTone = reliability === "high"
    ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
    : reliability === "medium"
      ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
      : "bg-rose-500/15 text-rose-700 dark:text-rose-400";

  const deltaKeys = deltaKeysFor(position);

  return (
    <div className="rounded-xl border bg-card/60 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <span
          className="h-2.5 w-2.5 rounded-full shrink-0"
          style={{ background: color }}
          aria-hidden
        />
        <span className="font-semibold text-sm truncate flex-1">{stat.display_name}</span>
        <span className="text-[11px] text-muted-foreground tabular-nums shrink-0">
          {stat.matches} {t("compare.matchesUnit")}
        </span>
        <span
          className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide shrink-0", reliabilityTone)}
          title={t(`compare.sample.${reliability}Tip`)}
        >
          {t(`compare.sample.${reliability}`)}
        </span>
      </div>
      <div className="text-[11px] text-muted-foreground tabular-nums">
        {t("compare.highlights.winsCount", { wins, total: strengthMetrics.length })}
      </div>
      {/* Forza + debolezza, sempre entrambe se ci sono i dati */}
      <div className="space-y-1">
        {best ? (
          <div className="flex items-start gap-1.5 text-[12px]">
            <Sparkles className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
            <span className="leading-snug">
              {t("compare.highlights.strength", {
                metric: best.label,
                value: best.fmt,
                gap: best.gapPct.toFixed(0),
              })}
            </span>
          </div>
        ) : (
          <div className="flex items-start gap-1.5 text-[12px] text-muted-foreground italic">
            <Sparkles className="h-3.5 w-3.5 shrink-0 mt-0.5 opacity-50" />
            <span>{t("compare.highlights.noStrength")}</span>
          </div>
        )}
        {worst ? (
          <div className="flex items-start gap-1.5 text-[12px]">
            <TrendingDown className="h-3.5 w-3.5 text-rose-500 shrink-0 mt-0.5" />
            <span className="leading-snug">
              {t("compare.highlights.weakness", {
                metric: worst.label,
                value: worst.fmt,
                gap: worst.gapPct.toFixed(0),
              })}
            </span>
          </div>
        ) : (
          <div className="flex items-start gap-1.5 text-[12px] text-muted-foreground italic">
            <TrendingDown className="h-3.5 w-3.5 shrink-0 mt-0.5 opacity-50" />
            <span>{t("compare.highlights.noWeakness")}</span>
          </div>
        )}
      </div>
      {/* Delta vs media squadra: 3 KPI del ruolo, con frecce e colore */}
      {benchmark && deltaKeys.length > 0 && (
        <div className="pt-2 border-t border-border/50">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1.5">
            {t("compare.highlights.vsTeamTitle")}
          </p>
          <div className="grid grid-cols-3 gap-2">
            {deltaKeys.map((k) => {
              const my = playerValueFor(stat, k, benchmark);
              const bench = benchmark[k] ?? 0;
              const diff = my - bench;
              const denom = Math.max(0.0001, Math.abs(bench));
              const pct = (diff / denom) * 100;
              const status = Math.abs(pct) < 5 ? "inline" : pct > 0 ? "above" : "below";
              const tone = status === "above"
                ? "text-emerald-600 dark:text-emerald-400"
                : status === "below"
                  ? "text-rose-600 dark:text-rose-400"
                  : "text-muted-foreground";
              const Icon = status === "above" ? TrendingUp : status === "below" ? TrendingDown : null;
              return (
                <div key={k} className="space-y-0.5">
                  <p className="text-[10px] text-muted-foreground truncate">
                    {t(`compare.metric.${kpiLabelKey(k)}`)}
                  </p>
                  <p className="text-sm font-semibold tabular-nums leading-tight">
                    {formatMetricValue(k, my)}
                  </p>
                  <p className={cn("text-[10px] inline-flex items-center gap-0.5 tabular-nums", tone)}>
                    {Icon && <Icon className="h-2.5 w-2.5" />}
                    {status === "inline"
                      ? t("compare.deltas.inline")
                      : `${pct > 0 ? "+" : ""}${pct.toFixed(0)}%`}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Mapping `DeltaKey` → suffisso usato in compare.metric.* (le chiavi del JSON
// non sono snake_case ma camelCase, qui faccio il match).
function kpiLabelKey(k: DeltaKey): string {
  switch (k) {
    case "avg_rating":        return "avgRating";
    case "win_rate":          return "winRate";
    case "mom_per_match":     return "momPct";
    case "pass_pct":          return "passPct";
    case "tackle_pct":        return "tacklePct";
    case "goals_per_match":   return "goalsPerMatch";
    case "assists_per_match": return "assistsPerMatch";
  }
}

// ──────────────────────────────────────────────────────────────────────────
// Tabella comparativa desktop (storica) - invariata nella logica.
// ──────────────────────────────────────────────────────────────────────────

type MetricDef = {
  key: keyof PositionStats | "draws";
  labelKey: string;
  fmt: (v: number) => string;
  betterIsHigher: boolean;
  benchmarkValue?: (b: Benchmark) => number | null;
};

function ComparisonTable({
  stats,
  position,
  benchmark,
  onOpenPlayer,
  t,
}: {
  stats: PositionStats[];
  position: EaPosition | "any";
  benchmark: Benchmark | null;
  onOpenPlayer: (id: string) => void;
  t: (k: string, opts?: any) => string;
}) {
  const metrics = useRoleMetrics(position);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[600px]">
        <thead>
          <tr className="text-[10.5px] uppercase tracking-wide text-muted-foreground border-b">
            <th className="text-left font-medium py-2 pr-3">{t("compare.table.metric")}</th>
            {stats.map((s) => (
              <th key={s.player_id} className="text-right font-medium py-2 px-2">
                <button
                  onClick={() => onOpenPlayer(s.player_id)}
                  className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                >
                  <span className="truncate max-w-[120px]">{s.display_name}</span>
                  <ChevronRight className="h-3 w-3 opacity-50" />
                </button>
                {s.is_guest && (
                  <p className="text-[9px] text-muted-foreground font-normal mt-0.5">
                    {t("compare.guestTag")}
                  </p>
                )}
              </th>
            ))}
            {benchmark && (
              <th className="text-right font-medium py-2 pl-2 text-muted-foreground italic">
                {t("compare.table.teamAvg")}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {metrics.map((m) => {
            const isRefinedMetric = BENCHMARK_KEYS.has(m.key as keyof PositionStats);
            // Per le metriche raffinate (rating, win_rate, ecc.) uso il valore
            // raffinato anche per il calcolo del "best"; per le metriche raw
            // (matches, goals, ecc.) il dato nudo è quello giusto.
            const values = stats.map((s) => statValueOf(s, m.key as keyof PositionStats, benchmark));
            const validValues = values.filter((v) => Number.isFinite(v));
            const best = validValues.length === 0
              ? null
              : m.betterIsHigher
                ? Math.max(...validValues)
                : Math.min(...validValues);
            const benchValue = benchmark && m.benchmarkValue ? m.benchmarkValue(benchmark) : null;
            return (
              <tr key={String(m.key)} className="border-b last:border-0">
                <td className="py-2 pr-3 text-[12.5px] text-muted-foreground">
                  {t(m.labelKey)}
                  {isRefinedMetric && (
                    <RefinedMark t={t} />
                  )}
                </td>
                {stats.map((s, idx) => {
                  const v = statValueOf(s, m.key as keyof PositionStats, benchmark);
                  const isBest = best !== null && Number.isFinite(v) && v === best && validValues.length > 1;
                  const refined = isRefinedMetric && isRefinementVisible(s, m.key as keyof Benchmark);
                  return (
                    <td
                      key={idx}
                      className={cn(
                        "py-2 px-2 text-right tabular-nums whitespace-nowrap",
                        isBest && "font-bold text-primary",
                      )}
                    >
                      {isBest && (
                        <Crown className="inline h-3 w-3 mr-1 text-primary" aria-hidden />
                      )}
                      {m.fmt(v)}
                      {refined && (
                        <span
                          className="text-primary/70 ml-0.5"
                          title={t("compare.refinement.markTip")}
                        >
                          *
                        </span>
                      )}
                    </td>
                  );
                })}
                {benchmark && (
                  <td className="py-2 pl-2 text-right tabular-nums whitespace-nowrap text-muted-foreground italic text-[12.5px]">
                    {benchValue != null ? m.fmt(benchValue) : "-"}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// MetricStack (mobile): una card per metrica con barre orizzontali per
// player. Sostituisce la tabella scrollabile orizzontalmente, che è una UX
// pessima su mobile.
// ──────────────────────────────────────────────────────────────────────────

function MetricStack({
  stats,
  position,
  benchmark,
  t,
}: {
  stats: PositionStats[];
  position: EaPosition | "any";
  benchmark: Benchmark | null;
  t: (k: string, opts?: any) => string;
}) {
  const metrics = useRoleMetrics(position);
  return (
    <Card className="shadow-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{t("compare.tableMobile.title")}</CardTitle>
        <CardDescription>{t("compare.tableMobile.desc")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {metrics.map((m) => {
          const isRefinedMetric = BENCHMARK_KEYS.has(m.key as keyof PositionStats);
          const values = stats.map((s) => statValueOf(s, m.key as keyof PositionStats, benchmark));
          const validValues = values.filter((v) => Number.isFinite(v));
          if (validValues.length === 0) return null;
          const best = m.betterIsHigher ? Math.max(...validValues) : Math.min(...validValues);
          const max = Math.max(...validValues, 0.0001);
          const benchValue = benchmark && m.benchmarkValue ? m.benchmarkValue(benchmark) : null;
          return (
            <div key={String(m.key)} className="rounded-lg border bg-card/60 p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium">
                  {t(m.labelKey)}
                  {isRefinedMetric && <RefinedMark t={t} />}
                </p>
                {benchValue != null && (
                  <p className="text-[10px] text-muted-foreground italic tabular-nums">
                    {t("compare.tableMobile.teamAvg")}: {m.fmt(benchValue)}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                {stats.map((s, idx) => {
                  const v = statValueOf(s, m.key as keyof PositionStats, benchmark);
                  const isBest = Number.isFinite(v) && v === best && validValues.length > 1;
                  const refined = isRefinedMetric && isRefinementVisible(s, m.key as keyof Benchmark);
                  // Larghezza barra: per metriche "lower is better" inverto la scala
                  // (chi è meglio = barra più piena) per coerenza visiva.
                  const pct = m.betterIsHigher
                    ? Math.min(100, (v / max) * 100)
                    : Math.min(100, max > 0 ? (1 - v / max) * 100 + 8 : 8);
                  return (
                    <div key={s.player_id} className="space-y-0.5">
                      <div className="flex items-center justify-between gap-2 text-xs">
                        <div className="flex items-center gap-1.5 min-w-0 flex-1">
                          <span
                            className="h-2 w-2 rounded-full shrink-0"
                            style={{ background: COLORS[idx % COLORS.length] }}
                            aria-hidden
                          />
                          <span className="truncate">{s.display_name}</span>
                          {isBest && <Crown className="h-3 w-3 text-primary shrink-0" />}
                        </div>
                        <span className={cn("tabular-nums shrink-0", isBest && "font-bold text-primary")}>
                          {m.fmt(v)}
                          {refined && (
                            <span
                              className="text-primary/70 ml-0.5"
                              title={t("compare.refinement.markTip")}
                            >
                              *
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted/60 overflow-hidden">
                        <div
                          className={cn(
                            "h-full rounded-full transition-all",
                            isBest ? "bg-primary" : "bg-foreground/30",
                          )}
                          style={{ width: `${pct}%`, background: isBest ? undefined : COLORS[idx % COLORS.length] }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

// Set di metriche del ruolo (comune+specifiche), centralizzato così la tabella
// desktop e lo stack mobile non vanno mai fuori sync.
function useRoleMetrics(position: EaPosition | "any"): MetricDef[] {
  const isGk = position === "goalkeeper";
  // I formatter usano sempre toFixed sui valori percentuali perché i valori
  // raffinati lato client (post-shrinkage) producono decimali infiniti che
  // bypassano l'arrotondamento server-side della RPC.
  const common: MetricDef[] = [
    { key: "matches", labelKey: "compare.metric.matches", fmt: (v) => String(v), betterIsHigher: true },
    { key: "avg_rating", labelKey: "compare.metric.avgRating", fmt: (v) => v.toFixed(2), betterIsHigher: true,
      benchmarkValue: (b) => b.avg_rating },
    { key: "win_rate", labelKey: "compare.metric.winRate", fmt: (v) => `${v.toFixed(1)}%`, betterIsHigher: true,
      benchmarkValue: (b) => b.win_rate },
    { key: "mom_per_match", labelKey: "compare.metric.momPct", fmt: (v) => `${v.toFixed(0)}%`, betterIsHigher: true,
      benchmarkValue: (b) => b.mom_per_match },
    { key: "minutes_avg", labelKey: "compare.metric.minutesAvg", fmt: (v) => `${(v ?? 0).toFixed(0)}'`, betterIsHigher: true },
    { key: "red_cards", labelKey: "compare.metric.redCards", fmt: (v) => String(v), betterIsHigher: false },
  ];
  const outfield: MetricDef[] = [
    { key: "goals", labelKey: "compare.metric.goals", fmt: (v) => String(v), betterIsHigher: true },
    { key: "goals_per_match", labelKey: "compare.metric.goalsPerMatch", fmt: (v) => v.toFixed(2), betterIsHigher: true,
      benchmarkValue: (b) => b.goals_per_match },
    { key: "assists", labelKey: "compare.metric.assists", fmt: (v) => String(v), betterIsHigher: true },
    { key: "assists_per_match", labelKey: "compare.metric.assistsPerMatch", fmt: (v) => v.toFixed(2), betterIsHigher: true,
      benchmarkValue: (b) => b.assists_per_match },
    { key: "shots", labelKey: "compare.metric.shots", fmt: (v) => String(v), betterIsHigher: true },
    { key: "pass_pct", labelKey: "compare.metric.passPct", fmt: (v) => v == null ? "-" : `${v.toFixed(1)}%`, betterIsHigher: true,
      benchmarkValue: (b) => b.pass_pct },
    { key: "tackle_pct", labelKey: "compare.metric.tacklePct", fmt: (v) => v == null ? "-" : `${v.toFixed(1)}%`, betterIsHigher: true,
      benchmarkValue: (b) => b.tackle_pct },
  ];
  const gk: MetricDef[] = [
    { key: "clean_sheets", labelKey: "compare.metric.cleanSheets", fmt: (v) => String(v), betterIsHigher: true },
    { key: "saves", labelKey: "compare.metric.saves", fmt: (v) => String(v), betterIsHigher: true },
    { key: "goals_conceded", labelKey: "compare.metric.goalsConceded", fmt: (v) => String(v), betterIsHigher: false },
  ];
  return [...common, ...(isGk ? gk : outfield)];
}
